// StoreForm — create/edit a customer store (My Account C1, task T7b,
// design.md AC7). Mirrors AddressForm.tsx's create/edit-mode convention
// exactly: one component, one `onSaved` callback covering both create and
// edit, `editingStore` prop switches to edit mode and pre-fills.
//
// ⚠️ AMENDED 2026-10-04 (account-area redesign, Task 7, Amendment
// 2026-10-02 F8/O3). Gained `showHeading` (default true): inside an
// EmbedDialog the dialog's own title carries the heading ("Add store" /
// "Edit store"), so the form's h3 would duplicate it — hosts pass
// showHeading={false} and the page-swap hosts keep the default. Everything
// else (fields, §70 trimming, If-Match edit, copy) is unchanged.
//
// Only `name` is required (model.CustomerStore — every address field is
// nullable). §70: name is trimmed on submit (store-api.ts's createStore/
// updateStore also trim — belt-and-suspenders, both idempotent) and NEVER
// lowercased; display always keeps the customer's casing.
//
// ⚠️ AMENDED 2026-10-06 (account-area redesign, Task 18, migration 145).
// Gained the optional Phone field (below Store Name): format-light by
// design — type="tel"/autocomplete="tel"/inputMode="tel" hint the keyboard
// and autofill, but the value is NEVER shape-validated here, matching the
// API's presence-only policy so international formats are not silently
// rejected. Trimmed on submit; a blank field is OMITTED from a create body
// (persists NULL).
//
// ⚠️ AMENDED 2026-10-10 (TD-597). ORIGINAL (verbatim): "a blank field is
// OMITTED from the request body, which persists NULL on create and leaves the
// stored value unchanged on PATCH (the same omitted-unchanged COALESCE
// semantics as every optional address field — the patch shape cannot express
// "clear to NULL")." CURRENT: the server now distinguishes an absent phone
// key (unchanged) from an explicit null/"" (clear to NULL), so emptying the
// phone of a store that HAD one sends `phone: null` on the PATCH; a blank
// phone on a store that never had one is still omitted.
//
// Inline styles only — no Tailwind. Every <button> is explicitly
// type="button"/type="submit" — the Cancel/Back button sits inside this
// component's own <form> and must not submit it (project rule §73's
// spirit).
//
// ⚠️ AMENDED 2026-10-09 (account-area redesign, Task 21 BASELINE). Every
// address input carries its WHATWG autofill token (address-line1/
// address-line2/address-level2/address-level1/postal-code) plus a `name`
// attribute mirroring the wire key it already submits — attributes only, no
// values/validation/wire change. The wire's `country` field has NO rendered
// control in this form (it travels optional with no input — pre-existing), so
// there is no country token to carry; "Store Name" is a business name with no
// token in the address set. The state control is a SELECT: the token is valid
// there too.

import { useState, useCallback, type FormEvent } from "react";
import {
  useEmbedTokens,
  EmbedField,
  EmbedInput,
  EmbedSelect,
  EmbedButton,
  EmbedBanner,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { createStore, updateStore } from "./store-api";
import type { CustomerStoreWire } from "./store-api";

// Exported since the device-count unit (Task 9): StorePicker.tsx's inline
// quick-add/draft capture renders the SAME state list — a second copy would
// silently drift from this one (rule 55 family).
export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

export interface StoreFormProps {
  /** Called with the full persisted store after a successful create OR
   *  edit — one callback name covers both modes. */
  onSaved: (store: CustomerStoreWire) => void;
  onBack: () => void;
  /** EDIT MODE: when set, fields pre-fill from this store and submit calls
   *  updateStore (PATCH, If-Match: version) instead of createStore (POST). */
  editingStore?: CustomerStoreWire;
  /** Render the form's own h3 ("Add store"/"Edit store"). Default true (the
   *  page-swap hosts). Dialog hosts pass false — the EmbedDialog title is
   *  the heading, and a duplicate h3 inside the dialog would be announced
   *  twice by screen readers. */
  showHeading?: boolean;
}

interface FieldState {
  name: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

function fieldsFromStore(store?: CustomerStoreWire): FieldState {
  return {
    name: store?.name ?? "",
    phone: store?.phone ?? "",
    line1: store?.line1 ?? "",
    line2: store?.line2 ?? "",
    city: store?.city ?? "",
    state: store?.state ?? "",
    postal_code: store?.postal_code ?? "",
    country: store?.country ?? "",
  };
}

export function StoreForm({ onSaved, onBack, editingStore, showHeading = true }: StoreFormProps) {
  const t = useEmbedTokens();
  const isEditing = !!editingStore;
  const [fields, setFields] = useState<FieldState>(() => fieldsFromStore(editingStore));
  const [nameError, setNameError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const updateField = useCallback((key: keyof FieldState, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    if (key === "name" && nameError) setNameError(null);
    if (apiError) setApiError(null);
  }, [nameError, apiError]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!fields.name.trim()) {
        setNameError("Store name is required.");
        return;
      }
      setSubmitting(true);
      setApiError(null);
      try {
        const patch = {
          name: fields.name,
          // TD-597. Blank is never sent as "". On CREATE it is omitted (NULL).
          // On a PATCH it depends on whether there is anything to clear: a
          // store that HAD a phone sends an explicit null (the server's clear
          // signal -- an omitted key means "unchanged"); a store that never
          // had one omits the key, so an untouched blank field sends nothing.
          phone: fields.phone.trim() || (editingStore?.phone ? null : undefined),
          line1: fields.line1.trim() || undefined,
          line2: fields.line2.trim() || undefined,
          city: fields.city.trim() || undefined,
          state: fields.state.trim() || undefined,
          postal_code: fields.postal_code.trim() || undefined,
          country: fields.country.trim() || undefined,
        };
        const saved = editingStore
          ? await updateStore(editingStore.id, editingStore.version, patch)
          // CREATE has nothing to clear: the null clear-signal is PATCH-only.
          : await createStore({ ...patch, phone: patch.phone ?? undefined });
        onSaved(saved);
      } catch (err) {
        setApiError(err instanceof ApiError ? err.message : "Failed to save store. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [fields, editingStore, onSaved],
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      {showHeading && (
        <h3 style={{ fontSize: t.fontSize.lg, fontWeight: 600, color: t.colors.text, margin: `0 0 ${t.spacing.md}px 0` }}>
          {isEditing ? "Edit store" : "Add store"}
        </h3>
      )}

      {apiError && (
        <div style={{ marginBottom: t.spacing.md }}>
          <EmbedBanner tone="error">{apiError}</EmbedBanner>
        </div>
      )}

      <EmbedField label="Store name" htmlFor="store-name" required error={nameError ?? undefined}>
        <EmbedInput
          id="store-name"
          type="text"
          value={fields.name}
          onChange={(e) => updateField("name", e.target.value)}
          aria-required="true"
          invalid={!!nameError}
          aria-invalid={!!nameError}
        />
      </EmbedField>

      {/* Format-light (Task 18): the tel hints shape the keyboard and let
          autofill offer the device's own number, but no pattern/validation
          runs here — the API accepts any non-empty value so international
          formats pass through untouched. */}
      <EmbedField label="Phone" htmlFor="store-phone">
        <EmbedInput
          id="store-phone"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          value={fields.phone}
          onChange={(e) => updateField("phone", e.target.value)}
        />
      </EmbedField>

      <EmbedField label="Address line 1" htmlFor="store-line1">
        <EmbedInput
          id="store-line1"
          name="line1"
          autoComplete="address-line1"
          type="text"
          value={fields.line1}
          onChange={(e) => updateField("line1", e.target.value)}
        />
      </EmbedField>

      <EmbedField label="Address line 2" htmlFor="store-line2">
        <EmbedInput
          id="store-line2"
          name="line2"
          autoComplete="address-line2"
          type="text"
          value={fields.line2}
          onChange={(e) => updateField("line2", e.target.value)}
          placeholder="Apt, suite, etc."
        />
      </EmbedField>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: t.spacing.md }}>
        <EmbedField label="City" htmlFor="store-city">
          <EmbedInput
            id="store-city"
            name="city"
            autoComplete="address-level2"
            type="text"
            value={fields.city}
            onChange={(e) => updateField("city", e.target.value)}
          />
        </EmbedField>
        <EmbedField label="State" htmlFor="store-state">
          <EmbedSelect
            id="store-state"
            name="state"
            autoComplete="address-level1"
            value={fields.state}
            onChange={(e) => updateField("state", e.target.value)}
          >
            <option value="">Select state</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </EmbedSelect>
        </EmbedField>
      </div>

      <div style={{ maxWidth: 200 }}>
        <EmbedField label="ZIP code" htmlFor="store-zip">
          <EmbedInput
            id="store-zip"
            name="postal_code"
            autoComplete="postal-code"
            type="text"
            inputMode="numeric"
            value={fields.postal_code}
            onChange={(e) => updateField("postal_code", e.target.value)}
          />
        </EmbedField>
      </div>

      <div style={{ display: "flex", gap: t.spacing.md, marginTop: t.spacing.lg }}>
        <EmbedButton type="button" variant="secondary" onClick={onBack} disabled={submitting}>
          Cancel
        </EmbedButton>
        <EmbedButton type="submit" variant="primary" disabled={submitting} loading={submitting}>
          {submitting ? "Saving..." : isEditing ? "Save changes" : "Add store"}
        </EmbedButton>
      </div>
    </form>
  );
}
